import invariant from 'invariant'
import {isNumber, join} from 'lodash-es'
import type {$ZodErrorMap} from 'zod/v4/core'


const plural = (count: number | bigint, zero: string, one: string, few: string, many: string) => {
  if (count === 0) return zero
  if (count === 1) return one
  if (count < 5) return few
  return many
}

const types = {
  function: 'funkciu',
  number: 'číslo',
  string: 'text',
  nan: 'nie je číslo',
  int: 'celé číslo',
  float: 'desatinné číslo',
  boolean: 'pravidvostnú hodnotu',
  date: 'dátum',
  bigint: 'veľké číslo',
  undefined: 'nedefinované',
  symbol: 'symbol',
  null: 'neexistujúcu hodnotu',
  array: 'pole',
  object: 'objekt',
  unknown: 'neznámu hodnotu',
  promise: 'prísľub',
  void: 'prázdny',
  never: 'nikdy',
  map: 'zobrazenie',
  set: 'množinu',
  custom: 'hodnotu',
  file: 'súbor',
  record: 'záznam',
  any: 'ľubovoľnú hodnotu',
  tuple: 'n-ticu',
  union: 'hodnotu z viacerých typov',
  intersection: 'hodnotu spĺňajúcu viac typov',
  enum: 'enum hodnotu',
  literal: 'konštantnú hodnotu',
  nullable: 'hodnotu alebo null',
  optional: 'nepovinnú hodnotu',
  nonoptional: 'povinnú hodnotu',
  success: 'platnú hodnotu',
  transform: 'transformovanú hodnotu',
  default: 'predvolenú hodnotu',
  prefault: 'predvolenú hodnotu',
  catch: 'zachytenú hodnotu',
  pipe: 'hodnotu v pipe',
  readonly: 'nemennú hodnotu',
  // eslint-disable-next-line camelcase
  template_literal: 'template literal',
  lazy: 'odloženú (lazy) hodnotu',
}

const zodErrorMap: $ZodErrorMap = (issue) => {
  let message = ''
  switch (issue.code) {
    case 'invalid_type':
      if (issue.input === undefined) {
        message = 'Povinné'
      } else {
        message = `Neplatná hodnota. Zadajte ${types[issue.expected]}`
      }
      break
    case 'too_small':
      if (issue.origin === 'array' || issue.origin === 'set') {
        message = `Pole musí obsahovať ${
          issue.exact ? 'presne' : issue.inclusive ? 'aspoň' : 'viac ako'
        } ${issue.minimum} ${plural(issue.minimum, 'prvkov', 'prvok', 'prvky', 'prvkov')}`
      } else if (issue.origin === 'string') {
        message = `Text musí obsahovať ${
          issue.exact ? 'presne' : issue.inclusive ? 'aspoň' : 'viac ako'
        } ${issue.minimum} ${plural(issue.minimum, 'znakov', 'znak', 'znaky', 'znakov')}`
      } else if (issue.origin === 'number' || issue.origin === 'int' || issue.origin === 'bigint') {
        message = `Číslo musí byť ${
          issue.exact
            ? 'presne '
            : issue.inclusive
              ? 'väčšie alebo rovné ako '
              : 'väčšie ako '
        }${issue.minimum}`
      } else if (issue.origin === 'date') {
        message = `Dátum musí byť ${
          issue.exact
            ? 'presne '
            : issue.inclusive
              ? 'väčší alebo rovný ako '
              : 'väčší ako '
        }${isNumber(issue.minimum) ? new Date(issue.minimum) : null}`
      } else message = 'Neplatná hodnota'
      break
    case 'too_big':
      if (issue.origin === 'array' || issue.origin === 'set') {
        message = `Pole musí obsahovať ${
          issue.exact ? 'presne' : issue.inclusive ? 'najviac' : 'menej ako'
        } ${issue.maximum} ${plural(issue.maximum, 'prvkov', 'prvok', 'prvky', 'prvkov')}`
      } else if (issue.origin === 'string') {
        message = `Text musí obsahovať ${
          issue.exact ? 'presne' : issue.inclusive ? 'najviac' : 'menej ako'
        } ${issue.maximum} ${plural(issue.maximum, 'znakov', 'znak', 'znaky', 'znakov')}`
      } else if (issue.origin === 'number' || issue.origin === 'int' || issue.origin === 'bigint') {
        message = `Číslo musí byť ${
          issue.exact
            ? 'presne '
            : issue.inclusive
              ? 'menšie alebo rovné ako'
              : 'menšie ako'
        } ${issue.maximum}`
      } else if (issue.origin === 'date') {
        message = `Dátum musí byť ${
          issue.exact
            ? 'presne '
            : issue.inclusive
              ? 'menší alebo rovný ako'
              : 'menší ako'
        } ${isNumber(issue.maximum) ? new Date(issue.maximum) : null}`
      } else message = 'Neplatná hodnota'
      break
    case 'invalid_format':
      if (issue.format === 'starts_with') {
        message = `Neplatná hodnota: musí začínať "${issue.pattern}"`
      } else if (issue.format === 'ends_with') {
        message = `Neplatná hodnota: musí končiť "${issue.pattern}"`
      } else if (issue.format === 'includes') {
        message = `Neplatná hodnota: musí obsahovať "${issue.pattern}"`
      } else if (issue.format === 'email') {
        message = 'Neplatný email'
      } else if (issue.format === 'url') {
        message = 'Neplatná URL adresa'
      } else if (issue.format === 'uuid') {
        message = 'Neplatné UUID'
      } else if (issue.format === 'datetime') {
        message = 'Neplatný dátum a čas'
      } else if (issue.format === 'date') {
        message = 'Neplatný dátum'
      } else if (issue.format === 'time') {
        message = 'Neplatný čas'
      } else if (issue.format === 'lowercase') {
        message = 'Hodnota musí byť iba malými písmenami'
      } else if (issue.format === 'uppercase') {
        message = 'Hodnota musí byť iba veľkými písmenami'
      } else {
        message = 'Neplatná hodnota'
      }
      break
    case 'unrecognized_keys':
      message = `Nerozpoznané kľúče v objekte: ${join(issue.keys, ', ')}`
      break
    case 'invalid_union':
      message = 'Neplatná hodnota'
      break
    case 'invalid_value':
      if (issue.input === undefined) {
        message = 'Povinné'
      } else {
        message = `Neplatná hodnota. Povolené hodnoty: ${JSON.stringify(issue.values)}`
      }
      break
    case 'not_multiple_of':
      message = `Číslo musí byť násobok ${issue.divisor}`
      break
    case 'invalid_key':
      message = 'Neplatná hodnota kľúča'
      break
    case 'invalid_element':
      message = 'Neplatný prvok v kolekcii'
      break
    case 'custom':
      message = issue.message || 'Neplatná hodnota'
      break
    default: invariant(false, `Invalid issue: ${issue satisfies never}`)
  }
  return {message}
}

export default zodErrorMap
